<template>
	<ul class="option-input" role="radiogroup" :aria-labelledby="labelId">
		<template v-for="(option, index) in optionsArray" :key="option.value">
			<!-- prettier-ignore-attribute @click -->
			<li
				:id="optionIdsComputed[index]"
				class="radio-option"
				role="radio"
				:aria-checked="option.value === modelValue"
				:aria-disabled="disabled"
				:data-value="option.value"
				tabindex="0"
				@click="
          if (!disabled) $emit('update:modelValue', option.value);
        "
				@keypress.space.prevent="
					if (!disabled) $emit('update:modelValue', option.value);
				"
			>
				<label :for="optionIdsComputed[index]">{{ option.label }}</label>
			</li>
		</template>
	</ul>
</template>

<script lang="ts" setup>
import type { AvailableOptionTypes } from "@/composables/useOptionsToArray";
import { useOptionsFlatMap } from "@/composables/useOptionsToArray";
import { getPseudoRandomId } from "@/helpers/logicHelpers";
import { computed } from "vue";

const props = defineProps<{
	options: AvailableOptionTypes;
	labelId?: string;
	modelValue?: string | number | boolean;
	disabled?: boolean;
}>();

defineEmits<
	(e: "update:modelValue", newValue?: string | number | boolean) => void
>();

const optionsArray = computed(() => useOptionsFlatMap(props.options));
const optionIdsComputed = computed(() =>
	optionsArray.value.map(getPseudoRandomId),
);
</script>

<style lang="scss">
.option-input {
	display: flex;
	flex-direction: row;
	list-style: none;
	padding: 0;

	--radio-border-radius: var(--border-radius);

	.radio-option {
		border: 1px solid var(--border);
		border-right-width: 0px;
		padding: 0.1875rem 0.5rem;
		user-select: none;
		cursor: pointer;
		margin: 0;
		display: flex;
		background: white;
		white-space: nowrap;

		label {
			pointer-events: none;
		}
	}

	.radio-option:hover {
		background: var(--background-primary-hover);
	}

	.radio-option:focus-visible {
		z-index: 2;
	}

	.radio-option:first-of-type {
		border-start-start-radius: var(--radio-border-radius);
		border-end-start-radius: var(--radio-border-radius);
	}

	.radio-option:last-of-type {
		border-right-width: 1px;
		border-start-end-radius: var(--radio-border-radius);
		border-end-end-radius: var(--radio-border-radius);
	}

	.radio-option[aria-checked="true"] {
		background: var(--hover-muted);
	}

	.radio-option:hover[aria-disabled="true"]:not([aria-checked="true"]) {
		background: inherit;
	}

	.radio-option[aria-disabled="true"] {
		box-shadow: none;
		cursor: default;
		color: var(--text-muted);
		pointer-events: none;
	}
}
</style>
